import { RevxJobSize } from 'components/RevxJobSize';
import { Divider, Typography } from 'antd';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { CheckSmallIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { ORDER_DATE_FORMAT, getFormatDate } from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import RevxJobImg from 'components/RevxJobImg';
import { RevxMoney } from 'components/RevxMoney';
import { PDFLink } from 'components/RevxPDFLink';
import { RevxShippingInfo } from 'components/RevxShippingInfo';
import {
  SectionHeading,
  Text,
  TextHeavy,
  UnderlineText,
} from 'components/RevxTypography';
import { CartItemProductParts, CartLineItem } from 'domains/cartDomain';
import { JobConfigGenerateTypeEnum } from 'domains/jobconfigDomain';
import { OrderStatusEnum } from 'domains/orderDomain';
import {
  JobCancellationStatusEnum,
  JobCardContacts,
  JobParticipantEnum,
  JobTypeEnum,
  JobUIStatusEnum,
  JobWorkflowStatusEnum,
  PDF_TEMPLATE_ENUM,
} from 'domains/workspaceDomain';
import { JobNum } from 'features/Workspace/components/JobNum';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { orderHistoryJobAnchorId } from '../useLocateOrderHistoryJob';
import { Link } from 'react-router-dom';
import workspaceService from 'services/jobService';
import { NOPROOF_DESCRIPTION } from 'utils/constants';
import {
  printingBackViewQuoteForShippingCart,
  printingFrontViewQuoteForShippingCart,
} from 'utils/jobConfig';
import { getFriendlyDisplay } from 'utils/misc';
import { isBuyer, isSupplier } from 'utils/rbac';
import './OrderHistoryLineItem.less';
import { InvoiceFileBlock } from 'components/InvoiceFileBlock';

const { PRODUCER, OPERATOR } = JobParticipantEnum;
const { INTERNAL } = JobTypeEnum;
const {
  PO_SENT,
  PO_READ,
  RFM_NEEDED,
  RFM,
  RFQ,
  PO_DECLINED,
  PO_ACCEPT,
  PREPARE_PROOF,
  PROOF_AWAIT_APPROVAL,
  PROOF_NEED_REVISION,
  IN_PRODUCTION,
  SHIPPED,
  DELIVERED,
  INVOICING,
} = JobWorkflowStatusEnum;

const artworkStatuses = [
  PO_SENT,
  PO_READ,
  RFM_NEEDED,
  RFM,
  RFQ,
  PO_DECLINED,
  PO_ACCEPT,
];
const proofingStatuses = [
  PREPARE_PROOF,
  PROOF_AWAIT_APPROVAL,
  PROOF_NEED_REVISION,
];
const shippedStatuses = [SHIPPED, DELIVERED, INVOICING];

const buyerJobStatusRouteMap: Record<string, JobUIStatusEnum | undefined> = {
  'Need Artwork': JobUIStatusEnum.NEED_ARTWORK,
  Proofing: JobUIStatusEnum.PROOFING,
  'In Production': JobUIStatusEnum.IN_PRODUCTION,
  Shipped: JobUIStatusEnum.SHIPPED,
  Completed: undefined,
};

const getBuyerJobStatus = (data: CartLineItem) => {
  const { workflowStatus, artworkConfirm } = data;
  if (workflowStatus && artworkStatuses.includes(workflowStatus)) {
    return Number(artworkConfirm) === 1 ? 'Proofing' : 'Need Artwork';
  }
  if (workflowStatus && proofingStatuses.includes(workflowStatus)) {
    return 'Proofing';
  }
  if (workflowStatus === IN_PRODUCTION) {
    return 'In Production';
  }
  if (workflowStatus && shippedStatuses.includes(workflowStatus)) {
    return 'Shipped';
  }
  return 'Completed';
};

type Props = {
  data: CartLineItem;
  orderStatus: OrderStatusEnum;
  jobContacts: JobCardContacts;
};

const OrderHistoryLineItem = ({ data, orderStatus, jobContacts }: Props) => {
  const { basisWeight: showBasisWeight } = useMeasurementUnit();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.app.user);
  const dispatch = useAppDispatch();

  const { participants, jobType } = data;

  // The order history endpoint fills createBy from the customer's default contact, which is a
  // plain map lookup server-side - it comes back null whenever that customer has none.
  const orderedByName = jobContacts?.createBy?.fullName;

  const onReorder = useCallback(async () => {
    if (user?.userId) {
      const res = await dispatch.workspace.reorder(data.jobId);
      if (res?.id) {
        if (data.configGenerateType === JobConfigGenerateTypeEnum.NORMAL) {
          window.location.href = routesMap.jobConfig.path + '/' + res?.id;
        } else {
          window.location.href =
            routesMap.jobConfig.path + '/expert/' + res?.id;
        }
      }
    }
  }, [data.jobId, dispatch.workspace, user?.userId, data.configGenerateType]);

  const productPartsRender = (arr: CartItemProductParts[]) => {
    return (arr || []).map((item) => {
      const title = [
        `${item.partName || t('shopcart.cover')}: ${showBasisWeight(
          item.paperBasisWeight,
          { metric: item.paperBasisWeightMetric }
        )} ${getFriendlyDisplay(item.paperCategory || '')}`,
        printingFrontViewQuoteForShippingCart(
          item.pmsFront,
          item.coatingFront,
          item.inkFront,
          item.skuName,
          item.skuTypeName
        ),
        printingBackViewQuoteForShippingCart(
          item.pmsBack,
          item.coatingBack,
          item.inkBack,
          item.skuName,
          item.skuTypeName
        ),
      ].join(' | ');
      return (
        <Fragment key={item.partName}>
          <div className="order-history-item-content-line order-history-item-content-omit">
            <Typography.Title
              className="order-history-item-content-line-ellipsis"
              ellipsis={{
                tooltip: title,
              }}
            >
              <TextHeavy className="order-history-item-content-line-title">
                {item.partName || t('shopcart.cover')}:
              </TextHeavy>
              <Text className="order-history-item-content-line-t">
                {`${showBasisWeight(item.paperBasisWeight, {
                  metric: item.paperBasisWeightMetric,
                })} ${getFriendlyDisplay(item.paperCategory || '')}`}
              </Text>
              <Divider type="vertical" />
              <Text className="order-history-item-content-line-t">
                {printingFrontViewQuoteForShippingCart(
                  item.pmsFront,
                  item.coatingFront,
                  item.inkFront,
                  item.skuName,
                  item.skuTypeName
                )}
              </Text>
              <Divider type="vertical" />
              <Text className="order-history-item-content-line-t">
                {printingBackViewQuoteForShippingCart(
                  item.pmsBack,
                  item.coatingBack,
                  item.inkBack,
                  item.skuName,
                  item.skuTypeName
                )}
              </Text>
            </Typography.Title>
          </div>
        </Fragment>
      );
    });
  };

  const productServiceRender = (services: string[]) => {
    const title = [
      `${t('shopcart.services')}: `,
      services.filter((item) => item).join(' | '),
    ];
    return (
      <>
        <div className="order-history-item-content-line order-history-item-content-omit">
          <Typography.Title
            className="order-history-item-content-line-ellipsis"
            ellipsis={{
              tooltip: title,
            }}
          >
            <TextHeavy className="order-history-item-content-line-title">
              {t('shopcart.services')}:
            </TextHeavy>
            {services.map((item, index) => {
              return (
                <span key={index}>
                  <Text className="order-history-item-content-line-t">
                    {item}
                  </Text>
                  {index !== services.length - 1 && <Divider type="vertical" />}
                </span>
              );
            })}
          </Typography.Title>
        </div>
      </>
    );
  };

  const services = useMemo(() => {
    return [
      ...data.product.services,
      data.digitalProofRequired ? '' : NOPROOF_DESCRIPTION,
    ].filter((item) => item);
  }, [data.product.services, data.digitalProofRequired]);

  const showOrderBy = useMemo(() => {
    return user?.userId !== data.customerId;
  }, [user, data]);

  const buyerJobStatus = useMemo(() => getBuyerJobStatus(data), [data]);
  const buyerJobStatusRoute = useMemo(() => {
    const status = buyerJobStatusRouteMap[buyerJobStatus];
    if (!status) {
      return undefined;
    }
    return `${routesMap.workspace.path}/${status}?jobId=${data.jobId}`;
  }, [buyerJobStatus, data.jobId]);

  const showInvoiceFile = () => {
    return true;
  };

  const sendInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={data.sendInvoiceFileUrl}
      title="Revshop Invoice"
      jobId={data.jobId}
      type="send"
      show={showInvoiceFile() && !!data.sendInvoiceFileUrl}
    />
  );

  const sendQuickBooksInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={data.sendQuickBooksInvoiceFileUrl}
      title="QuickBooks Invoice"
      jobId={data.jobId}
      type="sendQuickBooks"
      show={showInvoiceFile() && !!data.sendQuickBooksInvoiceFileUrl}
      invoiceNumber={data.invoiceNumber}
      batchUploadInvoiceNumber={data.batchUploadInvoiceNumber}
    />
  );

  const receiveInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={data.receiveInvoiceFileUrl}
      title="Receive Job Invoice"
      jobId={data.jobId}
      type="receive"
      show={showInvoiceFile() && !!data.receiveInvoiceFileUrl}
    />
  );

  const receiveQuickBooksInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={data.receiveQuickBooksInvoiceFileUrl}
      title="Receive QuickBooks Invoice"
      jobId={data.jobId}
      type="receiveQuickBooks"
      show={showInvoiceFile() && !!data.receiveQuickBooksInvoiceFileUrl}
    />
  );

  const batchUploadInvoiceBlock = data.batchUploadInvoiceNumber ? (
    <div style={{ textAlign: 'center', fontSize: '12px', color: '#666', marginTop: '2px' }}>
      Batch Invoice Number {data.batchUploadInvoiceNumber}
    </div>
  ) : null;

  return (
    <>
      <div
        hidden={
          data.cancellationStatus !== JobCancellationStatusEnum.IN_PROGRESS &&
          data.cancellationStatus !== JobCancellationStatusEnum.JOB_CANCELED
        }
      >
        <RevxFlexLayout
          align="center"
          justify="space-between"
          className="order-history-item-canceled"
        >
          <div>{`Job Canceled${
            data.cancellationStatus === JobCancellationStatusEnum.IN_PROGRESS
              ? ' - In Progress'
              : ''
          }`}</div>
          <RevxFlexLayout>
            <div className="order-history-item-canceled-date">
              {`Notified on `}
              {getFormatDate({
                dateStr: data.updateAt || '',
                formatStr: ORDER_DATE_FORMAT,
                isLocalTime: true,
              })}
            </div>
          </RevxFlexLayout>
        </RevxFlexLayout>
        {/* <JobCancelHeader data={data as unknown as JobItem} /> */}
      </div>
      <div
        className="order-history-item"
        id={orderHistoryJobAnchorId(data.jobId)}
      >
        <RevxFlexLayout align="top">
          <div className="order-history-item-img">
            {/* <img src={MockImg} alt="none" /> */}
            <RevxJobImg {...data} skuName={data.product.wrapperName} />
            <SectionHeading className="order-history-item-img-desc">
              {data.product.wrapperName}
            </SectionHeading>
            <Text type="secondary">
              <RevxJobSize
                isWorkspace
                size={data.product.finishedSizeIn}
                sizeMetric={data.product.finishedSizeMetric}
                sizePreset={data.product.finishedSizePreset}
                sizePresetUnit={data.product.finishedSizePresetUnit}
                orientation={data.product.orientation}
              />
            </Text>
          </div>
          <div className="order-history-item-content">
            <div className="order-history-item-content-line order-history-item-name-line">
              <div>
                <TextHeavy className="order-history-item-content-line-title">
                  {t('shopcart.jobNo')}:
                </TextHeavy>
                {isBuyer(user) && buyerJobStatusRoute ? (
                  <>
                    <Link
                      className="order-history-item-job-link"
                      to={buyerJobStatusRoute}
                    >
                      <JobNum data={data} disabled={true} />
                    </Link>
                    <Link
                      className="order-history-item-view-job-link"
                      to={buyerJobStatusRoute}
                    >
                      View Job
                    </Link>
                  </>
                ) : (
                  <JobNum data={data} disabled={true} />
                )}
                <Divider type="vertical" />
              </div>
              <SectionHeading
                className="order-history-item-name"
                ellipsis={{
                  tooltip: data.jobName,
                }}
              >
                {data.jobName}
              </SectionHeading>
              {isBuyer(user) && (
                <div className="order-history-item-buyer-status">
                  {buyerJobStatus}
                </div>
              )}
            </div>
            {productPartsRender(data.product.parts)}
            {services.length > 0 && <>{productServiceRender(services)}</>}
            <div className="order-history-item-content-line" hidden>
              <RevxFlexLayout>
                <span>
                  <UnderlineText color="green" style={{ fontSize: 10 }}>
                    <CheckSmallIcon style={{ marginRight: 8 }} />
                  </UnderlineText>
                  <UnderlineText color="blue" size="small">
                    {t('orderHistory.filesIncluded')} ({t('common.update')})
                  </UnderlineText>
                </span>
              </RevxFlexLayout>
            </div>
            {participants.some((v) => v === PRODUCER || v === OPERATOR) && (
              <RevxFlexLayout className="order-history-item-content-line order-history-item-content-omit">
                {showOrderBy && orderedByName && (
                  <>
                    <div>
                      <span className="bold">Ordered By:</span> {orderedByName}
                    </div>
                    <Divider type="vertical" />
                  </>
                )}
                {!(isSupplier(user) && jobType === INTERNAL) && (
                  <div className="mr-10">
                    <PDFLink
                      link={workspaceService.getPdfLink({
                        jobId: data.jobId,
                        jobConfigId: data.quoteId,
                        template: PDF_TEMPLATE_ENUM.PURCHASE_ORDER,
                      })}
                      title={t('workspace.pdfTitle.po')}
                    />
                  </div>
                )}

                <PDFLink
                  link={workspaceService.getPdfLink({
                    jobId: data.jobId,
                    jobConfigId: data.quoteId,
                    template: PDF_TEMPLATE_ENUM.JOB_TICKET_REVSHOP,
                  })}
                  title={t('workspace.pdfTitle.jobTicket')}
                />
              </RevxFlexLayout>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: '1px',
                flexWrap: 'wrap', // 在小屏幕上允许换行
              }}
            >
              <div style={{ display: 'flex', gap: '1px' }}>
                {sendInvoiceFileBlock}
                {sendQuickBooksInvoiceFileBlock}
              </div>
            </div>
            {batchUploadInvoiceBlock}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: '1px',
                flexWrap: 'wrap', // 在小屏幕上允许换行
              }}
            >
              {receiveInvoiceFileBlock}
              {receiveQuickBooksInvoiceFileBlock}
            </div>
          </div>
          <div className="order-history-item-right">
            <div className="order-history-item-price">
              <RevxFlexLayout align="center" justify="space-around">
                <div className="order-history-item-price-t">
                  <div className="order-history-item-price-t-title">
                    {t('shopcart.quantity')}:
                  </div>
                  <div className="order-history-item-price-t-value">
                    {data.quantity}
                  </div>
                </div>
                <Divider type="vertical" />
                <div className="order-history-item-price-t">
                  <div
                    className="order-history-item-price-t-title"
                    style={{ marginBottom: 5 }}
                  >
                    {t('orderHistory.totalCost')}:
                  </div>
                  <div className="order-history-item-price-t-value">
                    <span className="order-history-item-money">
                      <RevxMoney value={data.costLineItems.totalCharge} />
                    </span>
                  </div>
                </div>
                <Divider type="vertical" />
                {orderStatus === OrderStatusEnum.AWAITING_PAYMENT ? (
                  <Text className="order-history-item-awaiting-status">
                    {t('orderHistory.awaitingPayment')}
                  </Text>
                ) : orderStatus === OrderStatusEnum.PAYMENT_PROCESSING ? (
                  <Text className="order-history-item-awaiting-status">
                    {t('orderHistory.paymentProcessing')}
                  </Text>
                ) : (
                  <RevxButton size="small" type="primary" onClick={onReorder}>
                    {t('orderHistory.reorder')}
                  </RevxButton>
                )}
              </RevxFlexLayout>
            </div>
            <RevxFlexLayout className="order-history-item-date">
              <span>{t('shopcart.estimatedTitle')}</span>
              <div>
                <RevxShippingInfo shippingItems={data.jobCardShippingItems} />
              </div>
            </RevxFlexLayout>
          </div>
        </RevxFlexLayout>
      </div>
    </>
  );
};

export default OrderHistoryLineItem;
